import { Navigate, Route, Routes } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './features/auth/AuthProvider.js';
import { SignIn } from './features/auth/SignIn.js';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage.js';
import { AppLayout } from './components/AppLayout.js';
import { COMMANDER_ROLES, PEOPLE_ROLES, RequireRole, STOREROOM_ROLES, PLATOON_LEADER_ROLES, PERSONNEL_ROLES } from './components/RequireRole.js';
import { Home } from './components/Home.js';
import { OrgPage } from './features/units/OrgPage.js';
import { UnitRosterPage } from './features/units/UnitRosterPage.js';
import { SoldierDetailPage } from './features/users/SoldierDetailPage.js';
import { MyFilePage } from './features/users/MyFilePage.js';
import { ProfileRequestsPage } from './features/users/ProfileRequestsPage.js';
import { TemplatesPage } from './features/issuance/TemplatesPage.js';
import { ImportPage } from './features/users/ImportPage.js';
import { InventoryPage } from './features/inventory/InventoryPage.js';
import { UnitsRegistryPage } from './features/inventory/UnitsRegistryPage.js';
import { CatalogPage } from './features/inventory/CatalogPage.js';
import { IssuePage } from './features/issuance/IssuePage.js';
import { FormsPage } from './features/issuance/FormsPage.js';
import { FormDetailPage } from './features/issuance/FormDetailPage.js';
import { BulkIssuePage } from './features/issuance/BulkIssuePage.js';
import { PlatoonStorePage } from './features/distribution/PlatoonStorePage.js';
import { DistributePage } from './features/distribution/DistributePage.js';
import { RegisterPage } from './features/registration/RegisterPage.js';
import { RegistrationsPage } from './features/registration/RegistrationsPage.js';
import { SettingsPage } from './features/settings/SettingsPage.js';
import { ReportsPage } from './features/gear/ReportsPage.js';
import { AlertsPage } from './features/alerts/AlertsPage.js';
import { MissionsPage } from './features/missions/MissionsPage.js';
import { MissionDetailPage } from './features/missions/MissionDetailPage.js';
import { LedgerPage } from './features/ledger/LedgerPage.js';
import { ExportsPage } from './features/exports/ExportsPage.js';
import { DashboardPage } from './features/dashboard/DashboardPage.js';
import { NotificationsPage } from './features/notifications/NotificationsPage.js';
import { TransfersPage } from './features/transfers/TransfersPage.js';
import { useAppLanguage } from './i18n/useAppLanguage.js';

/**
 * The route table.
 *
 * Authentication gates the whole tree at once — an unauthenticated visitor sees
 * the sign-in screen and no route at all, rather than a set of routes that each
 * redirect. Role gating is per route, via `RequireRole`, and is a convenience
 * on top of server-side and rules-level checks (REQUIREMENTS §2.2).
 */
export function App() {
  const { t } = useTranslation();
  const { user, initialising } = useAuth();
  useAppLanguage();

  if (initialising) {
    return (
      <div className="flex min-h-full items-center justify-center text-slate-500">
        {t('home.loading')}
      </div>
    );
  }

  // The one page a stranger may see: the self-registration form (story 33).
  // Reachable signed in as well, so an admin can check what the link shows.
  if (window.location.pathname === '/register') {
    return <RegisterPage />;
  }
  // Where the emailed password-reset link and the invite land (story 43):
  // nobody is signed in yet, and the page must not depend on it.
  if (window.location.pathname === '/reset-password') {
    return <ResetPasswordPage />;
  }

  if (!user) {
    return <SignIn />;
  }

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route index element={<Home />} />
        {/* Where an email sign-in link lands; once signed in there is nothing to show here. */}
        <Route path="sign-in-link" element={<Navigate to="/" replace />} />
        <Route path="me" element={<MyFilePage />} />
        <Route
          path="profile-requests"
          element={
            <RequireRole allow={PERSONNEL_ROLES}>
              <ProfileRequestsPage />
            </RequireRole>
          }
        />
        <Route
          path="org"
          element={
            <RequireRole allow={PEOPLE_ROLES}>
              <OrgPage />
            </RequireRole>
          }
        />
        <Route
          path="org/:unitId"
          element={
            <RequireRole allow={PEOPLE_ROLES}>
              <UnitRosterPage />
            </RequireRole>
          }
        />
        <Route
          path="org/:unitId/soldier/:uid"
          element={
            <RequireRole allow={PEOPLE_ROLES}>
              <SoldierDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="registrations"
          element={
            <RequireRole allow={['admin']}>
              <RegistrationsPage />
            </RequireRole>
          }
        />
        <Route
          path="settings"
          element={
            <RequireRole allow={['admin']}>
              <SettingsPage />
            </RequireRole>
          }
        />
        <Route
          path="import"
          element={
            <RequireRole allow={['admin']}>
              <ImportPage />
            </RequireRole>
          }
        />
        <Route
          path="inventory"
          element={
            <RequireRole allow={STOREROOM_ROLES}>
              <InventoryPage />
            </RequireRole>
          }
        />
        <Route
          path="inventory/units"
          element={
            <RequireRole allow={STOREROOM_ROLES}>
              <UnitsRegistryPage />
            </RequireRole>
          }
        />
        <Route
          path="catalog"
          element={
            <RequireRole allow={STOREROOM_ROLES}>
              <CatalogPage />
            </RequireRole>
          }
        />
        <Route
          path="issue"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <IssuePage />
            </RequireRole>
          }
        />
        <Route
          path="templates"
          element={
            <RequireRole allow={STOREROOM_ROLES}>
              <TemplatesPage />
            </RequireRole>
          }
        />
        <Route
          path="issue/bulk"
          element={
            <RequireRole allow={[...STOREROOM_ROLES, ...PLATOON_LEADER_ROLES]}>
              <BulkIssuePage />
            </RequireRole>
          }
        />
        <Route
          path="platoon-store"
          element={
            <RequireRole allow={['platoon_sergeant']}>
              <PlatoonStorePage />
            </RequireRole>
          }
        />
        <Route
          path="platoon-store/distribute"
          element={
            <RequireRole allow={['platoon_sergeant']}>
              <DistributePage />
            </RequireRole>
          }
        />
        <Route
          path="reports"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <ReportsPage />
            </RequireRole>
          }
        />
        <Route
          path="alerts"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <AlertsPage />
            </RequireRole>
          }
        />
        <Route path="missions" element={<MissionsPage />} />
        <Route path="missions/:id" element={<MissionDetailPage />} />
        <Route
          path="ledger"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <LedgerPage />
            </RequireRole>
          }
        />
        <Route
          path="dashboard"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <DashboardPage />
            </RequireRole>
          }
        />
        <Route
          path="exports"
          element={
            <RequireRole allow={COMMANDER_ROLES}>
              <ExportsPage />
            </RequireRole>
          }
        />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="transfers" element={<TransfersPage />} />
        <Route path="forms" element={<FormsPage />} />
        <Route path="forms/:id" element={<FormDetailPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function NotFound() {
  const { t } = useTranslation();
  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
      <p className="text-slate-500">{t('errors.not_found')}</p>
    </section>
  );
}
